import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { cloneDeep, debounce } from "lodash";
import { AxiosResponse, AxiosError } from "axios";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useApplicationContext } from "../../application/ApplicationContext";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import {
  DocumentOfReceive,
  DocumentShort,
} from "../../models/DocumentOfReceive";
import { Warehouse } from "../../models/Warehouse";
import { Product } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Utility } from "../../core/Utility";
import { NewInvoiceIdForm } from "../../forms/InvoiceIdForm/InvoiceIdForm";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import {
  getAllDocuments,
  getOneDocument,
  deleteOneDocument,
} from "../../services/NewVersionService";
import "./NewRepromaterialInputPage.scss";

type MyFilters = {
  global: { value: string | null; matchMode: FilterMatchMode };
  finishedDocumentNumber: { value: string | null; matchMode: FilterMatchMode };
  documentPairId: { value: string | null; matchMode: FilterMatchMode };
  loadingCityName: { value: string | null; matchMode: FilterMatchMode };
  partnerName: { value: string | null; matchMode: FilterMatchMode };
  shipperName: { value: string | null; matchMode: FilterMatchMode };
  driverName: { value: string | null; matchMode: FilterMatchMode };
  vehicleNumber: { value: string | null; matchMode: FilterMatchMode };
  reproProducts: { value: string | null; matchMode: FilterMatchMode };
  reproAmounts: { value: string | null; matchMode: FilterMatchMode };
};

export const NewRepromaterialInputPage = () => {
  const {
    //load and set global filters
    state,
    selectedWarehouseSet,
    selectedDateFromSet,
    selectedDateToSet,
  } = useApplicationContext();

  const { t } = useTranslation();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = useMemo(
    () => new Date(businessYear, 0, 1, 0, 0, 1),
    [businessYear],
  );
  const yearEnd = useMemo(
    () => new Date(businessYear, 11, 31, 23, 59, 59),
    [businessYear],
  );
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    if (businessYear === currentYear) {
      const sevenDaysAgo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
        0,
        0,
        1,
      );
      const from = Utility.clampDateToRange(sevenDaysAgo, yearStart, yearEnd);
      const to = Utility.clampDateToRange(today, yearStart, yearEnd);
      return { from, to };
    }
    return { from: yearStart, to: yearEnd };
  };
  const [dateFrom, setDateFrom] = useState<Date>(() => {
    if (state.selectedDateFrom) {
      return Utility.clampDateToRange(
        new Date(state.selectedDateFrom),
        yearStart,
        yearEnd,
      );
    }
    return getDefaultRangeForBusinessYear().from;
  });
  const [dateTo, setDateTo] = useState<Date>(() => {
    if (state.selectedDateTo) {
      return Utility.clampDateToRange(
        new Date(state.selectedDateTo),
        yearStart,
        yearEnd,
      );
    }
    return getDefaultRangeForBusinessYear().to;
  });

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [selectedRepromaterialInput, setSelectedRepromaterialInput] =
    useState<DocumentOfReceive>();
  const [showForm, setShowForm] = useState<string>("");
  const tableRef = useRef<any>(null);

  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>(
    activeUser?.role?.name === RoleType.Warehouseman
      ? (activeUser?.warehouse as Warehouse)
      : ({} as Warehouse),
  );
  const [allRepromaterialInputDocuments, setAllRepromaterialInputDocuments] =
    useState<DocumentShort[]>([]);
  const [repromaterialInputDocuments, setRepromaterialInputDocuments] =
    useState<DocumentShort[]>([]);

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [inputToDelete, setInputToDelete] = useState<string>("");

  const [processing, setProcessing] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState<MyFilters>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    finishedDocumentNumber: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    documentPairId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    shipperName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reproProducts: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reproAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const debouncedSetFilters = useMemo(
    () =>
      debounce((nextFilters: MyFilters) => {
        setFilters(nextFilters);
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  useEffect(() => {
    const unwrapArray = (resp: any): any[] => {
      const d = resp?.data;
      if (d?.result && Array.isArray(d.result)) return d.result;
      if (Array.isArray(d)) return d;
      return [];
    };

    const mapReproNames = (docs: any[], productsArr: Product[]) => {
      const out: any[] = [];
      for (const oneDocument of docs) {
        const receivedProducts =
          oneDocument.reproProducts?.slice(0, -1).split(";") ?? [];
        let newProductNames = "";
        for (const oneProductId of receivedProducts) {
          newProductNames +=
            (productsArr.find((p) => p.id == oneProductId)?.name ??
              oneProductId) + ";";
        }
        newProductNames = newProductNames ? newProductNames.slice(0, -1) : "";
        const newProductAmounts = oneDocument.reproAmounts?.slice(0, -1) ?? "";

        out.push({
          ...oneDocument,
          reproProductIds: oneDocument.reproProducts,
          reproProducts: newProductNames === "undefined" ? "" : newProductNames,
          reproAmounts: newProductAmounts,
        });
      }
      return out;
    };

    (async () => {
      try {
        setProcessing(true);

        const [whResp, prodResp] = await Promise.all([
          getAllWarehouses(),
          getAllProducts(),
        ]);

        const wh = unwrapArray(whResp) as Warehouse[];
        const prod = unwrapArray(prodResp) as Product[];
        setWarehouses(wh);
        setProducts(prod);

        if (activeUser?.role?.name === RoleType.Warehouseman) {
          selectedWarehouseSet(activeUser?.warehouse as Warehouse);
        } else {
          if (state.selectedWarehouse)
            setSelectedWarehouse(state.selectedWarehouse);
        }

        const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
        const warehouseId = activeUser?.warehouse?.id;

        const from = dateFrom;
        const to = new Date(dateTo.getTime() + 24 * 60 * 60 * 1000);

        const response: AxiosResponse = await getAllDocuments(
          false, // repromaterial
          true, // inputs
          from,
          to,
          isWarehouseman,
          isWarehouseman ? warehouseId : undefined,
        );

        const allDocuments = response?.data?.result ?? [];
        const allFormatedDocuments = mapReproNames(allDocuments, prod);

        setAllRepromaterialInputDocuments(allFormatedDocuments);

        const filteredByWarehouse = state?.selectedWarehouse?.id
          ? allFormatedDocuments.filter(
              (item) => state.selectedWarehouse?.id == item.warehouseId,
            )
          : [...allFormatedDocuments];

        setRepromaterialInputDocuments(filteredByWarehouse);
      } catch (e) {
        console.log(e as AxiosError);
      } finally {
        setProcessing(false);
      }
    })();
  }, []);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedDateFromSet(from);
    selectedDateToSet(to);
  }, [businessYear, yearStart, yearEnd]);

  const toast = useRef(null);
  const navigate = useNavigate();

  const resetFilters = () => {
    //reset all other filters
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      finishedDocumentNumber: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      documentPairId: { value: null, matchMode: FilterMatchMode.CONTAINS },
      loadingCityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      shipperName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      driverName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      vehicleNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
      reproProducts: { value: null, matchMode: FilterMatchMode.CONTAINS },
      reproAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setGlobalFilterValue("");
  };

  useEffect(() => {
    formatRepromaterialDocumentsData(allRepromaterialInputDocuments);
    resetFilters();
  }, [selectedWarehouse]);

  useEffect(() => {
    setProcessing(true);
    const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
    const warehouseId = activeUser?.warehouse?.id;
    getAllDocuments(
      false, //repromaterial
      true, //inputs
      state?.selectedDateFrom
        ? new Date(state?.selectedDateFrom)
        : new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate() - 7,
            0,
            0,
            1,
          ),
      state?.selectedDateTo
        ? new Date(state?.selectedDateTo?.getTime() + 24 * 60 * 60 * 1000)
        : new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
      isWarehouseman,
      isWarehouseman ? warehouseId : undefined,
    )
      .then((response: AxiosResponse) => {
        const allDocuments = response.data.result;
        console.log(allDocuments);
        const allFormatedDocuments = [];
        for (const oneDocument of allDocuments) {
          if (
            new Date(oneDocument?.date).getTime() <
            (state?.selectedDateTo
              ? new Date(state?.selectedDateTo).getTime() +
                24 * 60 * 60 * 1000 -
                1
              : new Date().getTime())
          ) {
            const receivedProducts = oneDocument.reproProducts
              .slice(0, -1)
              .split(";");
            let newProductNames = "";
            for (const oneProductName of receivedProducts) {
              newProductNames +=
                products.find((item) => item.id == oneProductName)?.name + ";";
            }
            newProductNames = newProductNames.slice(0, -1);
            const newProductAmounts = oneDocument.reproAmounts.slice(0, -1);

            allFormatedDocuments.push({
              ...oneDocument,
              reproProductIds: oneDocument.reproProducts,
              reproProducts:
                newProductNames == "undefined" ? "" : newProductNames,
              reproAmounts: newProductAmounts,
            });
          }
        }
        setAllRepromaterialInputDocuments(allFormatedDocuments);
        // if (activeUser?.role?.name !== RoleType.Warehouseman) {
        //     setSelectedWarehouse({} as Warehouse);
        //     selectedWarehouseSet(undefined);
        // }
        formatRepromaterialDocumentsData(allFormatedDocuments);
        resetFilters();
      })
      .finally(() => setProcessing(false));
  }, [state.selectedDateFrom, state.selectedDateTo]);

  const formatRepromaterialDocumentsData = (allDocuments: DocumentShort[]) => {
    let copyOfRepromaterialDocuments: DocumentShort[] = [];

    for (const repromaterialInput of allDocuments) {
      //filtriranje prijemnica po magacinu
      if (
        selectedWarehouse?.id
          ? repromaterialInput.warehouseId == selectedWarehouse?.id
          : true
      ) {
        copyOfRepromaterialDocuments.push(repromaterialInput);
      }
    }
    copyOfRepromaterialDocuments.sort((a: DocumentShort, b: DocumentShort) => {
      return a.date < b.date ? 1 : -1;
    });
    setRepromaterialInputDocuments(copyOfRepromaterialDocuments);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehouses.find(
      (item) => item.id == value,
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse); //set local state
    selectedWarehouseSet(copyOfNewWarehouse); //set global state
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { from } = getDefaultRangeForBusinessYear();
      setDateFrom(from);
      selectedDateFromSet(from);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d > dateTo) {
      d = dateTo;
    }
    setDateFrom(d);
    selectedDateFromSet(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    if (!value) {
      const { to } = getDefaultRangeForBusinessYear();
      setDateTo(to);
      selectedDateToSet(to);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d < dateFrom) {
      d = dateFrom;
    }
    setDateTo(d);
    selectedDateToSet(d);
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const refreshDocuments = async () => {
    const isWarehouseman = activeUser?.role?.name === RoleType.Warehouseman;
    const warehouseId = activeUser?.warehouse?.id;
    const from = dateFrom;
    const to = new Date(dateTo.getTime() + 24 * 60 * 60 * 1000);

    setProcessing(true);
    try {
      const resp: AxiosResponse = await getAllDocuments(
        false,
        true,
        from,
        to,
        isWarehouseman,
        isWarehouseman ? warehouseId : undefined,
      );

      const allDocs = resp?.data?.result ?? [];

      // prepakuj reproProducts ids -> names
      const allFormatedDocuments = allDocs.map((oneDocument: any) => {
        const receivedProducts =
          oneDocument.reproProducts?.slice(0, -1).split(";") ?? [];
        let newProductNames = "";
        for (const oneProductId of receivedProducts) {
          newProductNames +=
            (products.find((p) => p.id == oneProductId)?.name ?? oneProductId) +
            ";";
        }
        newProductNames = newProductNames ? newProductNames.slice(0, -1) : "";
        const newProductAmounts = oneDocument.reproAmounts?.slice(0, -1) ?? "";

        return {
          ...oneDocument,
          reproProductIds: oneDocument.reproProducts,
          reproProducts: newProductNames === "undefined" ? "" : newProductNames,
          reproAmounts: newProductAmounts,
        };
      });

      setAllRepromaterialInputDocuments(allFormatedDocuments);
      formatRepromaterialDocumentsData(allFormatedDocuments);
      resetFilters();
    } finally {
      setProcessing(false);
    }
  };

  const accept = async () => {
    try {
      await deleteOneDocument(inputToDelete);
      setInputToDelete("");
      await refreshDocuments();

      //@ts-ignore
      toast.current.show({
        severity: "success",
        detail: t("Prijemnica je obrisana"),
        life: 3000,
      });
    } catch (e) {
      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: t("Greška pri brisanju"),
        life: 3000,
      });
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const renderHeader = () => {
    return (
      <div className="search-container">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Globalna pretraga..."
          />
        </span>
      </div>
    );
  };

  const formatDocumentNumber = (rowData: DocumentShort) => {
    const displayNumber =
      rowData.finishedDocumentNumber ??
      rowData.finishedDocumentId ?? // fallback na stari broj, za svaki slučaj
      "/";

    if (rowData.isDocumentFinished || rowData.finishedDocument) {
      return <span style={{ color: "#00A553" }}>{displayNumber}</span>;
    } else {
      return <span style={{ color: "#DC3545" }}>{displayNumber}</span>;
    }
  };

  const formatDate = (rowData: DocumentShort) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatControlHouseId = (rowData: DocumentShort) => {
    return (
      <div className="invoice-container">
        <Button
          id="buttonInputWithIconId"
          label={`${
            rowData?.controlHouseInvoiceId === null ||
            rowData?.controlHouseInvoiceId === ""
              ? "unesite"
              : rowData?.controlHouseInvoiceId
          }`}
          onClick={() => {
            getOneDocument(rowData.id).then((resp: any) => {
              const oneInput = resp?.data?.result ?? resp?.data;
              if (oneInput?.finishedDocument === true) {
                setSelectedRepromaterialInput(cloneDeep(oneInput));
                setShowForm(v4());
              } else {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    "Nije moguće uneti broj računa transporta za prijemnice koje nisu zaključene",
                  ),
                  life: 3000,
                });
              }
            });
          }}
          style={{
            border: "1px solid #ccc",
            backgroundColor: "#eee",
            color: "#000",
          }}
        />
      </div>
    );
  };

  const formatOptions = (rowData: DocumentShort) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/document-of-receive-repromaterial-preview-fast/${id}`);
          }}
        />
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Administrator ||
          activeUser?.role?.name === RoleType.Warehouseman ||
          activeUser?.role?.name === RoleType.Director ||
          activeUser?.role?.name === RoleType.FinishingCenter) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-pencil"
            onButtonClick={() => {
              const id = rowData.id;
              navigate(`/document-of-receive-repromaterial-fast/${id}`);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
        {(activeUser?.role?.name === RoleType.Superadmin ||
          activeUser?.role?.name === RoleType.Director) && (
          <ButtonInput
            addStyle={{ border: "none", background: "#198754" }}
            labelText={""}
            icon="bi bi-trash"
            onButtonClick={async () => {
              const id = rowData.id;
              setInputToDelete(id);
              setDeleteConfirmVisible(true);
            }}
            disabled={activeUser?.role?.name === RoleType.Director}
          />
        )}
      </div>
    );
  };

  const formatTransportId = (rowData: DocumentShort) => {
    return (
      <div className="invoice-container">
        <Button
          id="buttonInputWithIconId"
          label={`${
            rowData?.shipperInvoiceId === null ||
            rowData?.shipperInvoiceId === ""
              ? "unesite"
              : rowData?.shipperInvoiceId
          }`}
          onClick={() => {
            getOneDocument(rowData.id).then((resp: any) => {
              const oneInput = resp?.data?.result ?? resp?.data;
              if (oneInput?.finishedDocument === true) {
                setSelectedRepromaterialInput(cloneDeep(oneInput));
                setShowForm(v4());
              } else {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    "Nije moguće uneti broj računa transporta za prijemnice koje nisu zaključene",
                  ),
                  life: 3000,
                });
              }
            });
          }}
          style={{
            border: "1px solid #ccc",
            backgroundColor: "#eee",
            color: "#000",
          }}
        />
      </div>
    );
  };

  const exportToExcel = async () => {
    try {
      setProcessing(true);

      // 1. Get current filter values
      const currentDateFrom = state?.selectedDateFrom || dateFrom;
      const currentDateTo = state?.selectedDateTo || dateTo;
      const currentWarehouseId = selectedWarehouse?.id;

      // 2. Filter by date and warehouse
      let dataToExport = repromaterialInputDocuments.filter((item) => {
        const itemDate = new Date(item.date);
        const fromDate = new Date(currentDateFrom);
        const toDate = new Date(currentDateTo);
        toDate.setHours(23, 59, 59, 999);

        // Date filter
        if (itemDate < fromDate || itemDate > toDate) return false;

        // Warehouse filter
        if (currentWarehouseId && item.warehouseId !== currentWarehouseId)
          return false;

        return true;
      });

      // 3. Apply all active filters
      dataToExport = dataToExport.filter((item) => {
        // Check each filter field
        if (filters.finishedDocumentNumber?.value) {
          const docNumber = (
            item.finishedDocumentNumber ??
            item.finishedDocumentId ??
            ""
          ).toLowerCase();
          if (
            !docNumber.includes(
              filters.finishedDocumentNumber.value.toLowerCase(),
            )
          ) {
            return false;
          }
        }
        if (
          filters.documentPairId?.value &&
          !item.documentPairId
            ?.toLowerCase()
            .includes(filters.documentPairId.value.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.loadingCityName?.value &&
          !item.loadingCityName
            ?.toLowerCase()
            .includes(filters.loadingCityName.value.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.partnerName?.value &&
          !item.partnerName
            ?.toLowerCase()
            .includes(filters.partnerName.value.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.shipperName?.value &&
          !item.shipperName
            ?.toLowerCase()
            .includes(filters.shipperName.value.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.driverName?.value &&
          !item.driverName
            ?.toLowerCase()
            .includes(filters.driverName.value.toLowerCase())
        ) {
          return false;
        }
        if (
          filters.vehicleNumber?.value &&
          !item.vehicleNumber
            ?.toLowerCase()
            .includes(filters.vehicleNumber.value.toLowerCase())
        ) {
          return false;
        }
        if (filters.reproProducts?.value) {
          const productNames = item.reproProducts?.toLowerCase() || "";
          if (
            !productNames.includes(filters.reproProducts.value.toLowerCase())
          ) {
            return false;
          }
        }
        if (filters.reproAmounts?.value) {
          const amounts = item.reproAmounts?.toLowerCase() || "";
          if (!amounts.includes(filters.reproAmounts.value.toLowerCase())) {
            return false;
          }
        }
        return true;
      });

      // 4. Sort by date (oldest first)
      const sortedData = [...dataToExport].sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      // 5. Prepare Excel data
      const excelRepromaterialObject = sortedData.flatMap((item) => {
        const formatedProductNames = item?.reproProductIds
          ? item.reproProductIds.split(";").filter((name) => name.trim() !== "")
          : [];
        const units = item?.reproAmounts
          ? item.reproAmounts
              .split(";")
              .map((amount) => amount.match(/(PG|Kom|KG|TO|L|MVK)/i)?.[0] || "")
              .filter((unit) => unit.trim() !== "")
          : [];
        const productAmounts = item?.reproAmounts
          ? item.reproAmounts
              .replace(/(PG|Kom|KG|TO|L|MVK)/gi, "")
              .split(";")
              .filter((amount) => amount.trim() !== "")
              .map((amount) => {
                // Konvertuj u broj (zameni ',' sa '.' ako postoji)
                const num = parseFloat(amount.replace(",", "."));
                return isNaN(num) ? amount : num;
              })
          : [];

        return formatedProductNames.map((productId, i) => ({
          BrojDokumenta:
            item.finishedDocumentNumber ?? item.finishedDocumentId ?? "",
          BrOtprPrem: item.documentPairId,
          Datum: Utility.formatDate(new Date(item.date)),
          MestoUtovara: item.loadingCityName,
          MestoIstovara: item.warehouseName,
          Dobavljac: item.partnerName,
          Prevoznik: item.shipperName,
          Vozac: item.driverName,
          BrojRegistracije: item.vehicleNumber,
          NazivRobe: products.find((p) => p.id == productId)?.name || productId,
          Količina: productAmounts[i] || "",
          MernaJedinica: units[i] || "",
          Napomena: item.note,
          BrojRacunaTransporta: item.shipperInvoiceId,
          BrojRacunaKontrolneKuce: item.controlHouseInvoiceId,
        }));
      });

      // 6. Create and save Excel file
      const xlsx = await import("xlsx");
      const worksheet = xlsx.utils.json_to_sheet(excelRepromaterialObject);
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 10 }); // Column K (Količina)
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Set cell type to numeric
            // Ako je ceo broj - bez decimala, inače 2 decimale
            if (Number.isInteger(worksheet[cellAddress].v)) {
              worksheet[cellAddress].z = "0"; // Format za cele brojeve (npr. "5")
            } else {
              worksheet[cellAddress].z = "0.00"; // Format za decimale (npr. "3.14")
            }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const fileSaver = await import("file-saver");
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      fileSaver.default.saveAs(
        blob,
        `Ulaz repromaterijala ${Utility.formatDate(new Date())}.xlsx`,
      );
    } catch (error) {
      console.error("Greška pri eksportu:", error);
      // @ts-ignore
      toast.current.show({
        severity: "error",
        detail: t("Došlo je do greške pri eksportu podataka"),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatProducts = (rowData: DocumentShort) => {
    if (!rowData.reproProducts) return null;
    const products = rowData.reproProducts.split(";");
    return <div style={{ whiteSpace: "pre-line" }}>{products.join("\n")}</div>;
  };

  const formatAmounts = (rowData: DocumentShort) => {
    if (!rowData.reproAmounts) return null;
    const amounts = rowData.reproAmounts.split(";");
    return <div style={{ whiteSpace: "pre-line" }}>{amounts.join("\n")}</div>;
  };

  const productsFilterFunction = (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true;
    }
    if (value === undefined || value === null) {
      return false;
    }
    return value.reproProducts.toLowerCase().includes(filter.toLowerCase());
  };

  const amountsFilterFunction = (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true;
    }
    if (value === undefined || value === null) {
      return false;
    }
    return value.reproAmounts.toLowerCase().includes(filter.toLowerCase());
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Ulaz repromaterijala")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements-text">{`${t("u")}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehouses.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements"> </span>
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                // reset local states
                if (activeUser?.role?.name !== RoleType.Warehouseman) {
                  setSelectedWarehouse({} as Warehouse);
                  selectedWarehouseSet(undefined);
                }
                setDateFrom(from);
                setDateTo(to);
                // reset global date filters na default za poslovnu godinu
                selectedDateFromSet(from);
                selectedDateToSet(to);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem",
              }}
              labelText={t("Novi prijem")}
              icon="bi bi-truck"
              onButtonClick={() => {
                navigate(`/document-of-receive-repromaterial-fast/new`);
              }}
              disabled={
                activeUser?.role?.name === RoleType.Director ||
                activeUser?.role?.name === RoleType.Backoffice ||
                activeUser?.role?.name === RoleType.ControlHouse ||
                activeUser?.role?.name === RoleType.Logistician ||
                activeUser?.role?.name === RoleType.Commercialist ||
                activeUser?.role?.name === RoleType.CommercialistII
              }
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              ref={tableRef}
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              sortField="date"
              sortOrder={-1}
              filters={filters}
              filterDisplay="row"
              filterDelay={300}
              globalFilterFields={[
                "finishedDocumentNumber",
                "documentPairId",
                "loadingCityName",
                "partnerName",
                "shipperName",
                "driverName",
                "vehicleNumber",
                "reproProducts",
                "reproAmounts",
              ]}
              header={renderHeader()}
              emptyMessage="Nema prijemnica repromaterijala za zadate kriterijume"
              value={repromaterialInputDocuments}
              onFilter={(e) => {
                debouncedSetFilters(cloneDeep(e.filters as MyFilters));
              }}
              showGridlines
              stripedRows
            >
              <Column
                frozen
                body={formatDocumentNumber}
                field="finishedDocumentNumber"
                filterField={"finishedDocumentNumber"}
                filter
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="documentPairId"
                filter
                header={t("Br.otpr.prem.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatDate}
                sortable
                header={t("Datum")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="loadingCityName"
                filter
                header={t("Mesto utovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="warehouseName"
                header={t("Mesto istovara")}
                style={{ minWidth: "90px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="partnerName"
                sortable
                filter
                header={t("Dobavljač")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="shipperName"
                sortable
                filter
                header={t("Prevoznik")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="driverName"
                filter
                header={t("Vozač")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="vehicleNumber"
                filter
                header={t("Broj registracije")}
                style={{ minWidth: "80px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatProducts}
                sortable
                filter
                filterField="reproProducts"
                filterMatchMode="custom"
                filterFunction={productsFilterFunction}
                header={t("Naziv robe")}
                style={{ minWidth: "200px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatAmounts}
                sortable
                filter
                filterField="reproAmounts"
                filterMatchMode="custom"
                filterFunction={amountsFilterFunction}
                header={t("Količina")}
                style={{ minWidth: "200px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatTransportId}
                header={t("Br.rač.tr.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatControlHouseId}
                header={t("Br.rač.kon.")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "100px" }}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          </div>
        )}
      </div>
      <ConfirmDialog
        visible={deleteConfirmVisible}
        onHide={() => setDeleteConfirmVisible(false)}
        message={t("Da li ste sigurni da želite da obrišete prijemnicu?")}
        header={t("Potvrda brisanja prijemnice")}
        icon="bi bi-exclamation-circle"
        acceptLabel={`${t("Da")}`}
        acceptClassName="accept-button"
        accept={accept}
        rejectLabel={`${t("Ne")}`}
        rejectClassName="reject-button"
        reject={() => {}}
      />
      <NewInvoiceIdForm
        buttonLabel={``}
        buttonIcon="bi bi-boxes"
        show={showForm}
        selectedDocument={selectedRepromaterialInput}
        onSaved={refreshDocuments}
        buttonStyle={{
          border: "none",
          background: "#ffffff",
          color: "#6C757D",
          marginRight: "1rem",
        }}
        buttonSaveStyle={{ border: "none", background: "rgba(0, 165, 83, 1)" }}
        buttonResetStyle={{
          border: "none",
          background: "rgba(220, 53, 69, 1)",
        }}
      />
    </PageLayout>
  );
};
